import type { Lane } from "@/runtime/events";
import { runTransaction } from "@/storage/db";

export interface InstallCheckpoint {
  key: string;
  scope: string;
  file: string;
  lane: Lane;
  manifestId: string;
  filePath: string;
  segmentIndex: number;
  offset: number;
  bytes: number;
  segmentSha256: string;
  updatedAt: string;
}

function scopeKey(lane: Lane, manifestId: string): string {
  return `${lane}:${manifestId}`;
}

function fileKey(lane: Lane, manifestId: string, filePath: string): string {
  return `${lane}:${manifestId}:${filePath}`;
}

export function checkpointKey(
  lane: Lane,
  manifestId: string,
  filePath: string,
  segmentIndex: number,
): string {
  return `${lane}:${manifestId}:${filePath}:${segmentIndex}`;
}

export async function saveCheckpoint(record: InstallCheckpoint): Promise<void> {
  await runTransaction("installCheckpoints", "readwrite", async (store) => {
    await new Promise<void>((resolve, reject) => {
      const request = store.put(record);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  });
}

export async function getCheckpoint(
  lane: Lane,
  manifestId: string,
  filePath: string,
  segmentIndex: number,
): Promise<InstallCheckpoint | undefined> {
  return runTransaction("installCheckpoints", "readonly", async (store) => {
    return new Promise<InstallCheckpoint | undefined>((resolve, reject) => {
      const request = store.get(checkpointKey(lane, manifestId, filePath, segmentIndex));
      request.onsuccess = () => resolve((request.result as InstallCheckpoint | undefined) ?? undefined);
      request.onerror = () => reject(request.error);
    });
  });
}

export async function listCheckpointsForFile(
  lane: Lane,
  manifestId: string,
  filePath: string,
): Promise<InstallCheckpoint[]> {
  return runTransaction("installCheckpoints", "readonly", async (store) => {
    return new Promise<InstallCheckpoint[]>((resolve, reject) => {
      const index = store.index("file");
      const request = index.getAll(IDBKeyRange.only(fileKey(lane, manifestId, filePath)));
      request.onsuccess = () => resolve((request.result as InstallCheckpoint[]) ?? []);
      request.onerror = () => reject(request.error);
    });
  });
}

export async function listCheckpointsForLane(
  lane: Lane,
  manifestId: string,
): Promise<InstallCheckpoint[]> {
  return runTransaction("installCheckpoints", "readonly", async (store) => {
    return new Promise<InstallCheckpoint[]>((resolve, reject) => {
      const index = store.index("scope");
      const request = index.getAll(IDBKeyRange.only(scopeKey(lane, manifestId)));
      request.onsuccess = () => resolve((request.result as InstallCheckpoint[]) ?? []);
      request.onerror = () => reject(request.error);
    });
  });
}

export async function clearCheckpointsForFile(
  lane: Lane,
  manifestId: string,
  filePath: string,
): Promise<void> {
  await runTransaction("installCheckpoints", "readwrite", async (store) => {
    await new Promise<void>((resolve, reject) => {
      const index = store.index("file");
      const request = index.openCursor(IDBKeyRange.only(fileKey(lane, manifestId, filePath)));
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve();
          return;
        }
        cursor.delete();
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
    });
  });
}

export async function clearCheckpointsForLane(
  lane: Lane,
  manifestId: string,
): Promise<void> {
  await runTransaction("installCheckpoints", "readwrite", async (store) => {
    await new Promise<void>((resolve, reject) => {
      const index = store.index("scope");
      const request = index.openCursor(IDBKeyRange.only(scopeKey(lane, manifestId)));
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve();
          return;
        }
        cursor.delete();
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
    });
  });
}

export function makeCheckpoint(
  lane: Lane,
  manifestId: string,
  filePath: string,
  segmentIndex: number,
  offset: number,
  bytes: number,
  segmentSha256: string,
): InstallCheckpoint {
  return {
    key: checkpointKey(lane, manifestId, filePath, segmentIndex),
    scope: scopeKey(lane, manifestId),
    file: fileKey(lane, manifestId, filePath),
    lane,
    manifestId,
    filePath,
    segmentIndex,
    offset,
    bytes,
    segmentSha256,
    updatedAt: new Date().toISOString(),
  };
}
